import React, { useState, useRef, useEffect, type CSSProperties } from 'react'

interface AutoScrollProps {
  speed?: number // 滚动速度 (px/帧)
  gap?: number // 元素间距 (px)
  children: React.ReactNode
}

const AutoScroll = ({ speed = 1, gap = 8, children }: AutoScrollProps) => {
  const [isPaused, setIsPaused] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const translateX = useRef(0)
  const contentWidth = useRef(0)
  const animationId = useRef<number>()

  // 克隆子元素并计算内容宽度
  const childrenArray = React.Children.toArray(children)
  const duplicatedChildren = [...childrenArray, ...childrenArray]

  // 初始化内容宽度
  useEffect(() => {
    const updateWidth = () => {
      if (contentRef.current) {
        // 计算单个内容区块的宽度（总宽度/2）
        contentWidth.current = contentRef.current.scrollWidth / 2
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [children])

  // 动画循环（核心逻辑）
  const animate = () => {
    if (isPaused || !contentWidth.current) return

    translateX.current -= speed

    // 当滚动超过原始内容宽度时重置位置
    if (-translateX.current >= contentWidth.current) {
      translateX.current += contentWidth.current
    }

    if (contentRef.current) {
      contentRef.current.style.transform = `translateX(${translateX.current}px)`
    }

    animationId.current = requestAnimationFrame(animate)
  }

  // 控制动画启停
  useEffect(() => {
    animationId.current = requestAnimationFrame(animate)
    return () => {
      animationId.current && cancelAnimationFrame(animationId.current)
    }
  }, [isPaused])

  // 内容样式
  const contentStyle: CSSProperties = {
    display: 'inline-flex',
    willChange: 'transform',
  }

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden"
      onMouseEnter={() => {
        setIsPaused(true)
      }}
      onMouseLeave={() => {
        setIsPaused(false)
      }}
    >
      <div ref={contentRef} style={contentStyle}>
        {duplicatedChildren.map((child, index) => (
          <div
            key={`${index}-${React.isValidElement(child) ? child.key : index}`}
            style={{ flexShrink: 0 }}
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  )
}

export default AutoScroll
